import React, { useState, useEffect } from 'react';

interface Prefecture {
  id: number;
  name: string;
  code: string;
}

interface AreaSearchModalProps {
  show: boolean;
  selectedPrefectureIds: number[];
  onClose: () => void;
  onApply: (prefectureIds: number[]) => void;
  onImmediateSearch?: (prefectureIds: number[]) => void;
}

const AreaSearchModal: React.FC<AreaSearchModalProps> = ({
  show,
  selectedPrefectureIds,
  onClose,
  onApply,
  onImmediateSearch
}) => {
  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const [tempSelected, setTempSelected] = useState<number[]>(selectedPrefectureIds);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchPrefectures();
      setTempSelected(selectedPrefectureIds);
    }
  }, [show, selectedPrefectureIds]);

  const fetchPrefectures = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/prefectures');
      const data = await response.json();
      setPrefectures(data);
    } catch (error) {
      console.error('Failed to fetch prefectures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (prefectureId: number) => {
    const newSelected = tempSelected.includes(prefectureId)
      ? tempSelected.filter(id => id !== prefectureId)
      : [...tempSelected, prefectureId];
    
    setTempSelected(newSelected);
    
    // 即座に検索を実行
    if (onImmediateSearch) {
      onImmediateSearch(newSelected);
    }
  };

  const handleSelectAll = () => {
    const allIds = prefectures.map(p => p.id);
    setTempSelected(allIds);
    
    // 即座に検索を実行
    if (onImmediateSearch) {
      onImmediateSearch(allIds);
    }
  };

  const handleClearAll = () => {
    setTempSelected([]);
    
    // 即座に検索を実行
    if (onImmediateSearch) {
      onImmediateSearch([]);
    }
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  if (!show) {
    console.log('AreaSearchModal: show is false, not rendering');
    return null;
  }

  console.log('AreaSearchModal: rendering modal, show =', show);

  // 地域ごとにグループ化
  const regions = {
    '北海道・東北': ['北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県'],
    '関東': ['茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県'],
    '北陸・甲信越': ['新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県'],
    '東海': ['岐阜県', '静岡県', '愛知県', '三重県'],
    '関西': ['滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県'],
    '中国・四国': ['鳥取県', '島根県', '岡山県', '広島県', '山口県', '徳島県', '香川県', '愛媛県', '高知県'],
    '九州・沖縄': ['福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県']
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1070 
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1080,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        tabIndex={-1}
        onClick={(e) => {
          // モーダルコンテンツ外のクリックは無視
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">エリアを選択</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="閉じる"
              />
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* 選択/解除ボタン */}
                  <div className="d-flex gap-2 mb-3">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAll}
                    >
                      すべて選択
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleClearAll}
                    >
                      すべて解除
                    </button>
                    <div className="ms-auto text-muted small">
                      {tempSelected.length}件選択中
                    </div>
                  </div>

                  {/* 地域ごとの都道府県リスト */}
                  {Object.entries(regions).map(([regionName, prefNames]) => {
                    const regionPrefectures = prefectures.filter(p => prefNames.includes(p.name));
                    
                    return (
                      <div key={regionName} className="mb-4">
                        <h6 className="border-bottom pb-2 mb-3">{regionName}</h6>
                        <div className="row g-2">
                          {regionPrefectures.map(pref => (
                            <div key={pref.id} className="col-6 col-md-4 col-lg-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`pref-${pref.id}`}
                                  checked={tempSelected.includes(pref.id)}
                                  onChange={() => handleToggle(pref.id)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`pref-${pref.id}`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {pref.name}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleApply}
              >
                この条件で検索
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AreaSearchModal;

